'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { fetchCars } from '@/lib/supabase/dinauto-service';
import CarCard from '@/components/CarCard';
import CarDetailModal from '@/components/CarDetailModal';
import { useRouter } from 'next/navigation';
import type { Car } from '@/lib/supabase/dinauto-service';

export default function FeaturedCarsSection() {
  const router = useRouter();
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);
  const [featured, setFeatured] = useState<Car[]>([]);

  useEffect(() => {
    fetchCars().then((cars) => {
      // Pick 6 featured: 2 SUVs, 2 Comfort, 2 Economy — first available of each
      const suv = cars.filter((c) => c.category === 'SUV' && c.status !== 'Hidden').slice(0, 2);
      const comfort = cars.filter((c) => c.category === 'Comfort' && c.status !== 'Hidden').slice(0, 2);
      const economy = cars.filter((c) => c.category === 'Economy' && c.status !== 'Hidden').slice(0, 2);
      setFeatured([...suv, ...comfort, ...economy]);
    });
  }, []);

  const handleReserve = (car: Car) => {
    router.push(`/reservation?carId=${car.id}`);
  };

  return (
    <section className="py-24 bg-muted/40">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-primary" />
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Our Fleet</span>
            </div>
            <h2 className="font-black text-5xl leading-none" style={{ fontWeight: 900 }}>
              Popular<br />rentals.
            </h2>
          </div>
          <Link href="/our-cars-gallery" className="btn-outline gap-2 shrink-0">
            View All 30 Cars <ArrowRight size={16} />
          </Link>
        </div>

        {featured.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((car) => (
              <CarCard
                key={car.id}
                car={car}
                onReserve={handleReserve}
                onViewDetail={setSelectedCar}
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white border border-border h-72 animate-pulse" />
            ))}
          </div>
        )}
      </div>

      {selectedCar && (
        <CarDetailModal
          car={selectedCar}
          onClose={() => setSelectedCar(null)}
          onReserve={handleReserve}
        />
      )}
    </section>
  );
}